import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAdvisors } from '../../store/slices/advisorsSlice';
import './KnowAdvisors.scss';

const KnowAdvisors = () => {
  const dispatch = useDispatch();
  const { advisors, loading, error } = useSelector(state => state.advisors);
  const [filteredAdvisors, setFilteredAdvisors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch advisors on component mount
  useEffect(() => {
    dispatch(fetchAdvisors());
  }, [dispatch]);

  useEffect(() => {
    // Filter advisors based on search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = advisors.filter(advisor => 
        advisor.name?.toLowerCase().includes(term) || 
        advisor.designation?.toLowerCase().includes(term) ||
        advisor.publicDescription?.toLowerCase().includes(term) ||
        advisor.shortDescription?.toLowerCase().includes(term)
      );
      setFilteredAdvisors(filtered);
    } else {
      setFilteredAdvisors(advisors);
    }
  }, [searchTerm, advisors]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKnowMore = (advisor) => {
    setSelectedAdvisor(advisor);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdvisor(null);
  };

  return (
    <div className="know-advisors-container">
      <div className="page-header">
        <h1>Know Our Advisors</h1>
        <p>Meet the experts who guide Myelin's growth and vision</p>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search advisors..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading advisors...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>Error loading advisors: {error}</p>
          <button onClick={() => dispatch(fetchAdvisors())} className="retry-button">
            Retry
          </button>
        </div>
      ) : filteredAdvisors.length === 0 ? (
        <div className="no-results">
          <p>{searchTerm ? 'No advisors match your search criteria.' : 'No advisors found.'}</p>
        </div>
      ) : (
        <div className="advisors-grid">
          {filteredAdvisors.map(advisor => (
            <div key={advisor.id} className="advisor-card">
              <div className="advisor-image">
                {advisor.img ? (
                  <img src={advisor.img} alt={advisor.name} className="profile-image" />
                ) : (
                  <div className="avatar-placeholder">
                    {advisor.name?.charAt(0) || 'A'}
                  </div>
                )}
              </div>
              <div className="advisor-info">
                <h3>{advisor.name || 'Unnamed Advisor'}</h3>
                {advisor.designation && <p className="designation">{advisor.designation}</p>}
                <button 
                  className="know-more-btn"
                  onClick={() => handleKnowMore(advisor)}
                >
                  Know More
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Advisor Detail Modal */}
      {showModal && selectedAdvisor && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button className="close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="advisor-detail">
                <div className="advisor-detail-image">
                  {selectedAdvisor.img ? (
                    <img src={selectedAdvisor.img} alt={selectedAdvisor.name} className="profile-image-large" />
                  ) : (
                    <div className="avatar-placeholder-large">
                      {selectedAdvisor.name?.charAt(0) || 'A'}
                    </div>
                  )}
                </div>
                
                <div className="advisor-detail-info">
                  <h2>{selectedAdvisor.name || 'Unnamed Advisor'}</h2>
                  {selectedAdvisor.designation && (
                    <p className="designation-detail">{selectedAdvisor.designation}</p>
                  )}
                  
                  {selectedAdvisor.shortDescription && (
                    <div className="short-description-detail">
                      <h4>Overview</h4>
                      <div dangerouslySetInnerHTML={{ __html: selectedAdvisor.shortDescription }} />
                    </div>
                  )}
                  
                  {selectedAdvisor.publicDescription && (
                    <div className="bio-detail">
                      <h4>About</h4>
                      <div dangerouslySetInnerHTML={{ __html: selectedAdvisor.publicDescription }} />
                    </div>
                  )}
                  
                  {(selectedAdvisor.socialLinkedIn || selectedAdvisor.socialTwitter || selectedAdvisor.socialInstagram || selectedAdvisor.socialWeb) && (
                    <div className="social-links-detail">
                      <h4>Connect</h4>
                      <div className="social-buttons">
                        {selectedAdvisor.socialLinkedIn && (
                          <a href={selectedAdvisor.socialLinkedIn} target="_blank" rel="noopener noreferrer" className="social-btn linkedin">
                            LinkedIn
                          </a>
                        )}
                        {selectedAdvisor.socialTwitter && (
                          <a href={selectedAdvisor.socialTwitter} target="_blank" rel="noopener noreferrer" className="social-btn twitter">
                            Twitter
                          </a>
                        )}
                        {selectedAdvisor.socialInstagram && (
                          <a href={selectedAdvisor.socialInstagram} target="_blank" rel="noopener noreferrer" className="social-btn instagram">
                            Instagram
                          </a>
                        )}
                        {selectedAdvisor.socialWeb && (
                          <a href={selectedAdvisor.socialWeb} target="_blank" rel="noopener noreferrer" className="social-btn website">
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowAdvisors; 